import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Tags, Save } from "lucide-react";

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/admin/plans").then((r) => setPlans(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
  }, []);
  if (!plans || !settings) return <PageLoader />;

  const setField = (i, k, v) => setPlans(plans.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const savePlan = async (p) => {
    try {
      await api.put(`/admin/plans/${p.key}`, {
        key: p.key, name: p.name, price_monthly: Number(p.price_monthly), price_annual: Number(p.price_annual),
        storage_tb: Number(p.storage_tb), features: p.features, sort: p.sort,
      });
      toast.success(`${p.name} saved`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", {
        grace_pct: Number(settings.grace_pct), overage_per_tb: Number(settings.overage_per_tb),
        overage_enabled: !!settings.overage_enabled, pbs_repository: settings.pbs_repository,
        retention_after_cancel_days: Number(settings.retention_after_cancel_days),
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2"><Tags className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Plans & Pricing</h1></div>
      <p className="mb-5 text-sm text-slate-400">Edit every plan, price and quota without touching source code.</p>

      <div className="grid gap-4 lg:grid-cols-2">
        {plans.map((p, i) => (
          <GlassCard key={p.key} className="p-5" data-testid={`plan-editor-${p.key}`}>
            <div className="mb-3 flex items-center justify-between">
              <Input value={p.name} onChange={(e) => setField(i, "name", e.target.value)} className="max-w-[160px] bg-slate-950 border-slate-700 font-semibold" />
              <button onClick={() => savePlan(p)} data-testid={`save-plan-${p.key}`} className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-3 py-1.5 text-xs font-semibold text-[#06080D]"><Save className="h-3.5 w-3.5" /> Save</button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[["Monthly $", "price_monthly"], ["Annual $", "price_annual"], ["Storage TB", "storage_tb"]].map(([l, k]) => (
                <div key={k}><label className="text-xs text-slate-500">{l}</label><Input type="number" value={p[k]} onChange={(e) => setField(i, k, e.target.value)} className="bg-slate-950 border-slate-700" data-testid={`plan-${p.key}-${k}`} /></div>
              ))}
            </div>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="mt-6 p-6">
        <h2 className="mb-4 font-display font-semibold text-slate-100">Storage & policy settings</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Grace threshold %" val={settings.grace_pct} onChange={(v) => setSettings({ ...settings, grace_pct: v })} />
          <Field label="Overage $/TB-month" val={settings.overage_per_tb} onChange={(v) => setSettings({ ...settings, overage_per_tb: v })} />
          <Field label="Retention after cancel (days)" val={settings.retention_after_cancel_days} onChange={(v) => setSettings({ ...settings, retention_after_cancel_days: v })} />
          <div>
            <label className="text-xs text-slate-500">PBS Repository</label>
            <select value={settings.pbs_repository} onChange={(e) => setSettings({ ...settings, pbs_repository: e.target.value })} data-testid="pbs-repo-select" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
              <option>No-Subscription</option><option>Enterprise</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Auto overage</label>
            <select value={settings.overage_enabled ? "yes" : "no"} onChange={(e) => setSettings({ ...settings, overage_enabled: e.target.value === "yes" })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
              <option value="yes">Enabled</option><option value="no">Disabled</option>
            </select>
          </div>
        </div>
        <button onClick={saveSettings} data-testid="save-settings" className="mt-4 inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D]"><Save className="h-4 w-4" /> Save settings</button>
      </GlassCard>
    </div>
  );
}

function Field({ label, val, onChange }) {
  return <div><label className="text-xs text-slate-500">{label}</label><Input type="number" value={val} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" /></div>;
}
